import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ConclusionPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
  };

  const resetQuiz = () => {
    setSelectedAnswer(null);
    setShowResult(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">CF 생리학 연구</h1>
                <p className="text-sm text-gray-600">Life 마니또</p>
              </div>
            </Link>
            <nav className="flex items-center space-x-4">
              <Link to="/q4" className="text-gray-600 hover:text-gray-800 whitespace-nowrap">이전</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Title Section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              결론
            </h1>
            <p className="text-2xl text-gray-600">
              낭포성 섬유증이 아동에게 미치는 복합적 영향
            </p>
          </div>

          {/* Summary of Problems */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-file-list-3-line text-blue-600 mr-3"></i>
              낭포성 섬유증의 복합적 문제
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-600 font-bold text-sm">Q1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">CFTR 기능 장애</h3>
                    <p className="text-gray-600 text-sm">점액의 점도 증가</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 font-bold text-sm">Q2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">폐 기능 저하</h3>
                    <p className="text-gray-600 text-sm">만성 감염과 호흡 곤란</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-orange-600 font-bold text-sm">Q3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">소화 기능 장애</h3>
                    <p className="text-gray-600 text-sm">영양소 흡수 부족</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-purple-600 font-bold text-sm">Q4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">성장과 학습 영향</h3>
                    <p className="text-gray-600 text-sm">복합적 발달 지연</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Message */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-8 text-white mb-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <i className="ri-heart-line mr-3"></i>
              작업치료사의 역할
            </h2>
            <p className="text-lg leading-relaxed">
              낭포성 섬유증 아동은 이처럼 복잡한 생리학적 문제로 인해 학교생활에서 다양한 어려움을 겪을 수 있습니다. 
              저희 작업치료는 이러한 아동들이 만성적인 피로와 영양 문제에도 불구하고, 의미 있는 학교 활동(학습, 놀이, 또래 교류)에 
              최대한 참여할 수 있도록 돕는 역할을 합니다.
            </p>
          </div>

          {/* OT Intervention Areas */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              작업치료 중재 영역
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-book-open-line text-blue-600 text-xl"></i>
                </div>
                <h3 className="font-semibold text-blue-800 mb-3">학습 활동 지원</h3>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• 에너지 보존 기법</li>
                  <li>• 집중력 향상 전략</li>
                  <li>• 학습 환경 조정</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-team-line text-green-600 text-xl"></i>
                </div>
                <h3 className="font-semibold text-green-800 mb-3">사회적 참여</h3>
                <ul className="text-green-700 text-sm space-y-1">
                  <li>• 또래 관계 형성</li>
                  <li>• 그룹 활동 참여</li>
                  <li>• 자신감 향상</li>
                </ul>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-gamepad-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="font-semibold text-orange-800 mb-3">놀이와 여가</h3>
                <ul className="text-orange-700 text-sm space-y-1">
                  <li>• 적절한 활동 선택</li>
                  <li>• 체력 관리</li>
                  <li>• 즐거움 추구</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Principles */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              작업치료의 핵심 원칙
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-indigo-600 font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">개별화된 접근</h3>
                  <p className="text-gray-700 text-sm">각 아동의 증상 정도와 기능 수준에 맞춘 맞춤형 중재</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-indigo-600 font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">의미 있는 활동 중심</h3>
                  <p className="text-gray-700 text-sm">아동이 참여하고 싶어하는 활동을 중심으로 한 치료</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-indigo-600 font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">환경적 지원</h3>
                  <p className="text-gray-700 text-sm">학교와 가정 환경의 조정을 통한 참여 촉진</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-indigo-600 font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">다학제적 협력</h3>
                  <p className="text-gray-700 text-sm">의료진, 교사, 가족과의 긴밀한 협력</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quiz Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-question-line text-indigo-600 mr-3"></i>
              퀴즈: 발표 내용 확인
            </h2>
            
            {!showResult ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  낭포성 섬유증에서 점액이 끈끈해지는 근본적인 원인은 무엇인가요?
                </h3>
                
                <div className="space-y-3">
                  <button
                    onClick={() => handleAnswerSelect(1)}
                    className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <span className="font-medium text-blue-600 mr-3">1.</span>
                    폐에 세균이 많이 감염되어서
                  </button>
                  
                  <button
                    onClick={() => handleAnswerSelect(2)}
                    className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <span className="font-medium text-blue-600 mr-3">2.</span>
                    CFTR 채널의 기능 장애로 염소와 물의 분비가 감소해서
                  </button>
                  
                  <button
                    onClick={() => handleAnswerSelect(3)}
                    className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <span className="font-medium text-blue-600 mr-3">3.</span>
                    췌장에서 소화효소가 너무 많이 나와서
                  </button>
                  
                  <button
                    onClick={() => handleAnswerSelect(4)}
                    className="w-full text-left p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <span className="font-medium text-blue-600 mr-3">4.</span>
                    영양소 흡수가 잘 안되어서
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                {selectedAnswer === 2 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center justify-center mb-4">
                      <i className="ri-check-circle-line text-green-600 text-4xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-green-800 mb-2">정답입니다!</h3>
                    <p className="text-green-700">
                      발표를 잘 들어주셔서 감사합니다.
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center justify-center mb-4">
                      <i className="ri-close-circle-line text-red-600 text-4xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-red-800 mb-2">아쉽게도 틀렸습니다.</h3>
                    <p className="text-red-700 mb-4">
                      정답은 <strong>2번</strong>입니다. 낭포성 섬유증의 근본 원인은 CFTR 채널의 기능 장애로, 
                      이로 인해 염소와 물의 분비가 감소하여 점액이 끈끈해집니다.
                    </p>
                  </div>
                )}
                
                <button
                  onClick={resetQuiz}
                  className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  다시 풀기
                </button>
              </div>
            )}
          </div>

          {/* References Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              참고자료
            </h2>
            <div className="space-y-3 text-gray-700">
              <p className="leading-relaxed">
                1. Cystic Fibrosis Foundation Patient Registry 2021 Annual Data Report
              </p>
              <p className="leading-relaxed">
                2. 낭성 섬유증(CF) - 아동의 건강 문제 - MSD 매뉴얼 - 일반인용
              </p>
              <p className="leading-relaxed">
                3. Allergy Asthma Respir Dis 8(1):3-8, January 2020 https://doi.org/10.4168/aard.2020.8.1.3
              </p>
              <p className="leading-relaxed">
                4. https://medlineplus.gov/genetics/condition/cystic-fibrosis/
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-12">
            <Link
              to="/q4"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span>이전: Q4</span>
            </Link>
            <div className="text-gray-500 text-sm">
              발표 완료
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="text-center text-gray-600">
            <p className="text-sm mb-2">
              © 2025 Life 마니또 팀 | 낭포성 섬유증 생리학 연구 프로젝트
            </p>
            <p className="text-xs">
              참고 자료: MSD Manuals, MedlinePlus Genetics
            </p>
            <p className="text-xs mt-2">
              <a href="https://readdy.ai/?origin=logo" className="text-blue-600 hover:underline">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConclusionPage;